import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";

import APChip from "../../components/APChip";
import APButton from "../../components/buttons/APButton";
import APContactHelpButton from "../../components/buttons/APContactHelpButton";
import APCard from "../../components/cards/APCard";
import { APCardColor } from "../../components/cards/APCard";
import { formatDate } from "../../helpers/dates";
import { useAuth } from "../../providers/AuthProvider";
import {
  Dossier,
  DossierStatusColorMap,
  DossierStatusLabelMap,
} from "../../types/Dossier";
import { accountTextColorMap } from "../viewDossiers";

const EmployerDossierCard = ({
  dossier,
  color,
  canTerminate,
  canViewTermination,
  hideAccount = false,
}: {
  dossier: Dossier;
  color: APCardColor;
  canTerminate: boolean;
  canViewTermination: boolean;
  hideAccount?: boolean;
}) => {
  const navigate = useNavigate();
  const { getScopedPageUrl } = useAuth();

  const renderBodyContent = () => {
    return (
      <Stack spacing={0.5}>
        {dossier.opportunity?.catchEContractId && (
          <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
            <Typography variant="bodySmall">
              Contract ID: {dossier.opportunity.catchEContractId}
            </Typography>
          </Box>
        )}
        {dossier.endDate && dossier.endDate !== "" && (
          <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
            <Typography variant="bodySmall">
              Novation End: {formatDate(dossier.endDate)}
            </Typography>
          </Box>
        )}
        {dossier.opportunity?.parentAccountName && !hideAccount && (
          <Box>
            <Typography variant="bodySmall" color={accountTextColorMap[color]}>
              {dossier.opportunity.parentAccountName}
            </Typography>
          </Box>
        )}
      </Stack>
    );
  };

  const renderExpandContent = () => {
    return (
      <Stack spacing={0.5}>
        {dossier.contact?.employeeid && (
          <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
            {dossier.contact?.employeeid && (
              <Typography variant="bodySmall">
                ID: {dossier.contact.employeeid}
              </Typography>
            )}
          </Box>
        )}
        {dossier.startDate && (
          <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
            <Typography variant="bodySmall">
              Novation Start: {formatDate(dossier.startDate)}
            </Typography>
          </Box>
        )}
        {dossier.vehicleRegistrationNumber && (
          <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
            <Typography variant="bodySmall">
              Rego: {dossier.vehicleRegistrationNumber}
            </Typography>
          </Box>
        )}
        <Box display="flex" flexDirection="column" gap={1} pt={2}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <APContactHelpButton
              regardingOpportunityId={dossier.opportunity?.id ?? undefined}
            />
            <Box>
              {dossier.status === "Active" && canTerminate && (
                <Box>
                  <APButton
                    text="Request Termination"
                    variant="contained"
                    color="secondary"
                    onClick={() =>
                      navigate(
                        getScopedPageUrl("employerTerminateDossier", dossier.id)
                      )
                    }
                  />
                </Box>
              )}
              {(dossier.status === "Closing" || dossier.status === "Closed") &&
                canViewTermination &&
                dossier.terminationId && (
                  <Box>
                    <APButton
                      text="View"
                      variant="contained"
                      onClick={() =>
                        navigate(
                          getScopedPageUrl(
                            "employerViewDossierTermination",
                            dossier.id
                          )
                        )
                      }
                    />
                  </Box>
                )}
            </Box>
          </Box>
        </Box>
      </Stack>
    );
  };

  const renderTitleLeft = () => {
    return (
      <>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 0.5,
            alignItems: "center",
          }}
        >
          <Typography variant="titleMedium">
            {dossier.crmContactName}
          </Typography>
          <Typography variant="bodySmall">
            {" - "}
            {dossier.vehicleMakeName} {dossier.vehicleModelName}
          </Typography>
        </Box>
      </>
    );
  };

  const renderTitleRight = () => {
    return (
      <APChip
        text={DossierStatusLabelMap[dossier.status]}
        color={DossierStatusColorMap[dossier.status]}
        captialiseText={false}
      />
    );
  };

  return (
    <APCard
      titleLeft={renderTitleLeft()}
      bodyContent={renderBodyContent()}
      expandContent={renderExpandContent()}
      titleRight={renderTitleRight()}
      color={color}
      expandOnCardClick={true}
      variant="outlined"
    />
  );
};

export default EmployerDossierCard;
